import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { Hidden, LazyImage } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import bannerCarousel from "@NA/Handlers/ADIGetBannerCarouselHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { FC, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import parse from "html-react-parser";
import ADIColors from "@NA/Utils/ADIColors";
import { bannerGaTag,bannerButtonGaTag } from "@NA/Utils/GtmUtility";
 
const enum fields {
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    normalFontSize = "normalFontSize",
}
 
const mapStateToProps = (state: CustomApplicationState) => {
    const currentSplitPath = getLocation(state).pathname.split("/");
    const currentPath = currentSplitPath[currentSplitPath.length - 1];
    return {
        currentpage: currentPath,
        bannerCarouselList: state.ADIGlobalData.bannerCarouselStore.bannerCarousel?.bannerCarouselList,
        isLoading: state.ADIGlobalData.bannerCarouselStore.isLoading,
    };
};
 
const mapDispatchToProps = {
    bannerCarousel,
};
 
interface OwnProps extends WidgetProps {
    fields: {
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.normalFontSize]: number;
    };
    extendedStyles?: SlideshowStyles;
}
 
export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    headingMobileText?: TypographyPresentationProps;
    mobImage?: InjectableCss;
    subheadingMobileText?: TypographyPresentationProps;
    dealsHeadingText?: TypographyPresentationProps;
    dealsSubheadingText?: TypographyPresentationProps;
    dealsHeadingMobileText?: TypographyPresentationProps;
    dealsSubheadingMobileText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    slideMobileButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    contentWarpper?: InjectableCss;
    textContainer?: InjectableCss;
    buttonContainer?: InjectableCss;
    imageWarpper?: InjectableCss;
    imageModileWarpper?: InjectableCss;
    contentMobileWarpper?: InjectableCss;
}
 
export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            padding: 10px 0;
            position: relative;
        `,
    },
   
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 70,
        color: `${ADIColors.blue2}`,
        css: css`
            > svg {
                color: ${ADIColors.blue2};
            }
            top: -1.875em;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: ${getColor("common.background")};
            background-repeat: no-repeat;  
            background-size: 100% 100% !important;          
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
            min-width: 160px;
            height: 60px;
            padding: 19px 10px;
            font-size: 16px;
            box-shadow: inset 0 -4px 0 0 ${ADIColors.blue1};
            border-radius: 6px;
            text-transform: uppercase;
            font-family: "Nunito Sans", sans-serif;
            font-weight:800;
            color: #ffffff;
            span{
                font-weight:800 !important;
            color: #ffffff;
            }
        `,
    },
    slideMobileButton: {
        css: css`
            width: fit-content;
            padding: 9px 40px;
            bottom: 40px;
            margin-top: 42px;
            box-shadow: inset 0 -4px 0 0 ${ADIColors.blue1};
            background: ${ADIColors.blue5};
            color: ${getColor("common.background")};
            padding: 9px 50px;
            min-width: 180px;
            height: 40px;
            font-family: "Nunito Sans", sans-serif;
            text-transform: uppercase;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -24px;
            position: absolute;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            min-height: 9px;
            height: 13px;
            width: 13px;
            padding: 0;
            margin: 0 4px;
            border: 1px solid ${ADIColors.blue2};
            border-radius: 50%;
            background: ${getColor("common.border")};
            &:hover {
                background: ${ADIColors.white};
            }
            &:active {
                background: ${ADIColors.blue2};
            }
            &:focus {
                background: ${ADIColors.blue2};
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            right: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    headingText: {
        css: css`
            font-size: 46px;
            line-height: 52px;
            text-align: left;
            font-family: "Nunito Sans", sans-serif; 
            //Do not hard code font color here its dependent on theme background it will come from theme          
        `,
    },
    subheadingText: {
        css: css`
            font-size: 20px;
            line-height: 26px;
            font-weight: 400;
            margin-top: 2px;
            text-align: left;
            font-family: "Nunito Sans", sans-serif;
            //Do not hard code font color here its dependent on theme background it will come from theme            
        `,
    },
    headingMobileText: {
        css: css`
            font-size: 34px;
            line-height: 34px;
            margin-bottom: 5px;
            color: ${ADIColors.darkBlue5};
        `,
    },
    subheadingMobileText: {
        css: css`
            font-size: 18px;
            line-height: 20px;
            margin-bottom: 7px;
            font-weight: 400;
            color: ${ADIColors.darkBlue5};
        `,
    },
    dealsHeadingText: {
        css: css`
            font-size: 48px;
            font-weight: 900;
            line-height: 49px;
            width: 416px;
            white-space: normal;
            height: auto;
            margin: 0;
        `,
    },
    dealsSubheadingText: {
        css: css`
            font-size: 20px !important;
            line-height: 1.2 !important;
        `,
    },
    dealsHeadingMobileText: {
        css: css``,
    },
    contentWarpper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-top: 20px;
            margin-left: 31px;
            justify-content: space-between;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-basis: 100%;
                        margin-top: 0px;
                        margin-left: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    textContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-bottom: 55px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-basis: 100%; ;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    buttonContainer: {
        css: css``,
    },
    contentMobileWarpper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-top: 20px;
            text-align: left;
        `,
    },
    imageWarpper: {
        css: css`
            max-height: 222px;
            max-width: 321px;
            margin-top: 20px;
            margin-right: 31px;
        `,
    },
    imageModileWarpper: {},
};
 
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
 
export const Slideshow: FC<Props> = ({ fields, extendedStyles, bannerCarousel, currentpage, bannerCarouselList, isLoading }) => {
    const history = useHistory();
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);
 
    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);
 
    useEffect(() => {
        bannerCarousel({ isPreview: false, pageUrl: currentpage });
    }, [currentpage]);
 
    useEffect(() => {
        if (!embla) {
            return;
        }
 
        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);
 
    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }
 
        embla.scrollNext();
    }, [embla]);
 
    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);
 
    useEffect(() => {
        if (!embla || !bannerCarouselList || bannerCarouselList.length === 0) {
            return;
        }
 
        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, bannerCarouselList]);
 
    const onClick = (link: string | undefined, isNewTab?: boolean) => {
        if (isNewTab) {
            window.open(link, "_blank");
        } else if (link) {
            history.push(link);
        }
    };
 
    if (!bannerCarouselList || bannerCarouselList.length === 0 || isLoading) {
        return null;
    }
   
    const textAlignStyles = `text-align: ${fields.textAlignment};`;  
 
    const dealsPageContainer = {
        css: css`
            overflow: hidden;
            border-radius: ${currentpage === "Deals" ? "6px" : ""};
        `,
    };    
   
    return (
        <StyledWrapper {...styles.slideshowWrapper}>
            {fields.showArrows && bannerCarouselList.length > 1 && (
                <StyledWrapper {...styles.prevArrowWrapper}>
                    <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                        <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                    </Button>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.carouselContainer} {...dealsPageContainer} ref={emblaRef}>
                <StyledWrapper {...styles.slideContainerWrapper}>                    
                    {(bannerCarouselList as any[]).map((slide:any, index:any) => {
                        const responsiveImageBehaviorStyles = "background-size: cover;";
                        const backgroundStyles = `background-image: url(${slide.backgroundImage});`;
                        const focalPointStyles = getFocalPointStyles("center");
                        const overlayPositioningStyles = "align-items: stretch;";
                        let fontSizeStyles;
 
                        if (fields.responsiveFontSizes || fields.customFontSizes) {
                            fontSizeStyles = responsiveStyleRules(
                                fields.responsiveFontSizes,
                                fields.customFontSizes ? fields : undefined,
                            );
                        }
                                   
                        const slideWrapperStyles = {
                            css: css`
                                ${styles.slideContentWrapper?.css || ""}
 
                                ${textAlignStyles}
                                ${backgroundStyles}
                                ${responsiveImageBehaviorStyles}
                                ${focalPointStyles}
                                ${overlayPositioningStyles}
                                ${fontSizeStyles}
                                ${({ theme }: { theme: BaseTheme }) =>
                                    breakpointMediaQueries(theme, [
                                        css`
                                            background-image: unset;
                                        `,
                                        null,
                                        null,
                                        null,
                                        null,
                                    ])}
                            `,
                        };
                        const headingStyle = {
                            css: css`
                                ${styles.headingText?.css || ""}
                                ${slide.cssClass === "light"
                                    ? `color: ${ADIColors.black2};`
                                    : `color: ${ADIColors.white}`}
                                ${currentpage === "Deals" && styles.dealsHeadingText.css}
                            `,
                        };
                        const subHeadingStyle = {
                            css: css`
                                ${styles.subheadingText?.css || ""}
                                ${slide.cssClass === "light"
                                    ? `color: ${ADIColors.black2};`
                                    : `color: ${ADIColors.white}`}
                                ${currentpage === "Deals" && styles.dealsSubheadingText.css}
                            `,
                        };
                        const slideOverlayWrapperStyles = {
                            css: css`
                                ${styles.slideOverlayWrapper?.css || ""}
                                padding: 56px 16px 49px 51px;
                                display: flex;
                                width: 100%;
                                justify-content: space-between;
                                ${({ theme }: { theme: BaseTheme }) =>
                                    breakpointMediaQueries(theme, [
                                        css`
                                            padding: 0;
                                        `,
                                        null,
                                        null,
                                        null,
                                        null,
                                    ])}
                            `,
                        };
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <StyledWrapper                              
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                {...slideWrapperStyles}                                
                                onClick={() => onClick(slide.bannerLinkUrl, slide.isNewTab)}
                            >
                                <StyledWrapper {...slideOverlayWrapperStyles} data-ga-promo={bannerGaTag(slide)}>
                                    {/* Desktop */}
                                    <Hidden
                                        below="sm"                                                                              
                                        css={css`
                                            display: flex;
                                            flex-basis: 100%;
                                            justify-content: space-between;
                                        `}
                                    >                                        
                                        <StyledWrapper {...styles.contentWarpper}>                                            
                                            <StyledWrapper {...styles.textContainer}>
                                                {slide.headerLineOne && (
                                                    <Typography {...headingStyle}>
                                                        {currentpage === "Deals"
                                                            ? parse(slide.headerLineOne, parserOptions)
                                                            : slide.headerLineOne}
                                                    </Typography>
                                                )}
                                                {slide.headerLineTwo && (
                                                    <Typography {...headingStyle}>
                                                        {currentpage === "Deals"
                                                            ? parse(slide.headerLineTwo, parserOptions)
                                                            : slide.headerLineTwo}
                                                    </Typography>
                                                )}
 
                                                {slide.copyTextOne && (
                                                    <Typography {...subHeadingStyle}>{slide.copyTextOne}</Typography>
                                                )}
 
                                                {slide.copyTextTwo && (
                                                    <Typography {...subHeadingStyle}>{slide.copyTextTwo}</Typography>
                                                )}
                                                {slide.copyTextThree && (
                                                    <Typography {...subHeadingStyle}>{slide.copyTextThree}</Typography>
                                                )}
                                            </StyledWrapper>
 
                                            <StyledWrapper {...styles.buttonContainer}>
                                                <Button {...styles.slideButton} variant="primary" data-ga-promo={bannerButtonGaTag(slide)}>
                                                    {slide.bannerButtonText}
                                                </Button>                                              
                                            </StyledWrapper>
                                        </StyledWrapper>
 
                                        <StyledWrapper {...styles.imageWarpper}>
                                            <LazyImage altText={slide.imageAlternativeText} src={slide.imageURL} />
                                        </StyledWrapper>
                                    </Hidden>
                                    {/* mobile */}
                                    <Hidden
                                        above="sm"
                                        css={css`
                                            display: flex;
                                            flex-basis: 100%;
                                            flex-direction: column;
                                        `}
                                    >
                                        <StyledWrapper>
                                            <LazyImage
                                                {...styles.mobImage}
                                                altText={slide.imageAlternativeText}
                                                data-ga-promo={bannerGaTag(slide)}
                                                src={slide.centerBoxBackground}
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.contentMobileWarpper}>
                                            {slide.headerLineOne && (
                                                <Typography {...styles.headingMobileText}>
                                                    {currentpage === "Deals"
                                                        ? parse(slide.headerLineOne, parserOptions)
                                                        : slide.headerLineOne}
                                                </Typography>
                                            )}
                                            {slide.headerLineTwo && (
                                                <Typography {...styles.headingMobileText}>
                                                    {currentpage === "Deals"
                                                        ? parse(slide.headerLineTwo, parserOptions)
                                                        : slide.headerLineTwo}
                                                </Typography>
                                            )}
 
                                            <Typography {...styles.subheadingMobileText}>
                                                {slide.copyTextOne}
                                            </Typography>
                                            <Typography {...styles.subheadingMobileText}>
                                                {slide.copyTextTwo}
                                            </Typography>
                                            <Typography {...styles.subheadingMobileText}>
                                                {slide.copyTextThree}
                                            </Typography>
                                            <Button {...styles.slideMobileButton} data-ga-promo={bannerButtonGaTag(slide)} variant="primary">
                                                {slide.bannerButtonText}
                                            </Button>
                                        </StyledWrapper>
                                    </Hidden>
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            </StyledWrapper>
            {fields.slideIndicator && bannerCarouselList.length > 1 && (
                <StyledWrapper {...styles.dotsContainerWrapper}>
                    {(bannerCarouselList as any[])?.map((_:any, i:number) => {
                        const dotColorStyles =
                            i === selectedIndex
                                ? `
                                    background:  ${ADIColors.blue2};
                                    &:hover { background: white; }
                                    &:active { background: ${ADIColors.blue2}; }
                                `
                                : "";
                        const dotButtonStyles = {
                            css: css`
                                ${styles.dotButton?.css || ""}
                                ${dotColorStyles}
                            `,
                        };
                        return <Button key={i} {...dotButtonStyles} onClick={() => embla && embla.scrollTo(i)} />;
                    })}
                </StyledWrapper>
            )}
            {fields.showArrows && bannerCarouselList.length > 1 && (
                <StyledWrapper {...styles.nextArrowWrapper}>
                    <Button {...styles.nextArrowButton} onClick={() => embla && embla.scrollNext()}>
                        <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
 
const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};
 
const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};
 
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Slideshow),
    definition: {
        group: "Basic",
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
        ],
    },
};
 
export default widgetModule;